import { TreeNodeExpanded } from 'business/models/CustomModels';
import { TreeNodeDTO } from 'business/models/Models';
import TableSortInputFilter from 'components/customComponents/Controls/tableSortInputFilter';
import { exludedNodesList } from 'components/searchAndFilter/search/SearchPathSelector';
import { EExtraFieldNames } from 'enums/EExtraFieldNames';
import React from 'react';
import {
  ExplorerNode,
  DetailViewSortColumn,
  SortDirection,
  EDetailViewCols,
  EDetailViewFilterTypes,
} from './constants';
import { RMSvgIconNames } from 'components/customComponents/CommonComponents/RMSvgIcon';
import EViewType from 'enums/EViewType';

const handleTableFilter = (filterable: boolean, filterTitle: string) => (event: any) =>
  <TableSortInputFilter event={event} title={filterTitle} showSearchInput={filterable} />;

export const options: {
  accessor: string;
  Header: string;
  Filter?: (event: any) => JSX.Element;
  className?: string;
}[] = [
  {
    accessor: 'name',
    Header: 'File Name',
    Filter: handleTableFilter(false, 'File Name'),
  },
  {
    accessor: 'originalLocation',
    Header: 'Original Location',
    Filter: handleTableFilter(false, 'Original Location'),
  },
  {
    accessor: 'dateDeleted',
    Header: 'Date Deleted',
    Filter: handleTableFilter(false, 'Date Deleted'),
  },
  {
    accessor: 'type',
    Header: 'Type',
    Filter: handleTableFilter(false, 'Type'),
  },
];

/**
 * Prepares a tree node for rendering:
 * - Skips excluded node types
 * - Adds folder arrow flag if the node has children
 * - Recursively processes child nodes
 * @returns the transformed node or null if excluded.
 */
export const filterAndTransformNode = (node: TreeNodeDTO) => {
  if (exludedNodesList.includes(node.type as any)) {
    return null;
  }
  if (node.hasChildren) {
    node.extra = { ...(node.extra ?? {}), [EExtraFieldNames.ShowExpandArrow]: true };
  }
  const { children } = node as TreeNodeExpanded;
  return {
    ...node,
    children: children?.map((child) => filterAndTransformNode(child)).filter((x) => x),
  };
};

const SQL_DEFAULT_DATE_MONTHS: Record<string, string> = {
  Jan: '01',
  Feb: '02',
  Mar: '03',
  Apr: '04',
  May: '05',
  Jun: '06',
  Jul: '07',
  Aug: '08',
  Sep: '09',
  Oct: '10',
  Nov: '11',
  Dec: '12',
};

// SQL Server's default (unstyled) datetime-to-string conversion, e.g. "Jun 25 2025 4:21PM" -
// this is what the search endpoint's unformatted DateTime.ToString() gets translated to, and
// isn't parseable by the JS Date constructor, so extract the date parts directly from the string.
const SQL_DEFAULT_DATE_REGEX = /^([A-Za-z]{3})\s+(\d{1,2})\s+(\d{4})/;

// Matches the dd/MM/yyyy format already used for lastImaged/dateDispensed (see NodeDetailView).
export const formatDatePrepared = (value: string): string => {
  if (!value) {
    return value;
  }
  const sqlMatch = value.match(SQL_DEFAULT_DATE_REGEX);
  const sqlMonth = sqlMatch && SQL_DEFAULT_DATE_MONTHS[sqlMatch[1]];
  if (sqlMatch && sqlMonth) {
    return `${sqlMatch[2].padStart(2, '0')}/${sqlMonth}/${sqlMatch[3]}`;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}/${date.getFullYear()}`;
};

export const findOldestByDate = (arr: any[], dateKey: string): string => {
  return arr?.reduce((oldest, item) => {
    const value = item?.[dateKey];
    if (!value) {
      return oldest;
    }
    const date = new Date(value);

    if (!oldest) {
      return value;
    }

    return date < new Date(oldest) ? value : oldest;
  }, '');
};

export const findNewestByDate = (arr: any[], dateKey: string): string => {
  return arr?.reduce((newest, item) => {
    const value = item?.[dateKey];
    if (!value) {
      return newest;
    }
    const date = new Date(value);

    if (!newest) {
      return value;
    }

    return date < new Date(newest) ? newest : value;
  }, '');
};

export const sortByFileName = (
  a: ExplorerNode,
  b: ExplorerNode,
  direction: SortDirection,
): number => {
  if (direction === null) {
    return 0;
  }
  const comparison = (a.name || '').localeCompare(b.name || '');
  return direction === 'asc' ? comparison : -comparison;
};

export const parseDDMMYYYYdate = (value: string): Date => {
  const [day, month, year] = value.split('/').map(Number);
  return new Date(year, month - 1, day);
};

export const sortByDate = (
  a: ExplorerNode,
  b: ExplorerNode,
  dateKey: string,
  direction: SortDirection,
): number => {
  if (!direction) {
    return 0;
  }

  const aHasDate = Boolean(a[dateKey]);
  const bHasDate = Boolean(b[dateKey]);

  if (!aHasDate && !bHasDate) {
    return 0;
  }
  if (!aHasDate) {
    return 1;
  }
  if (!bHasDate) {
    return -1;
  }

  const dateA = parseDDMMYYYYdate(a[dateKey]).getTime();
  const dateB = parseDDMMYYYYdate(b[dateKey]).getTime();

  const comparison = dateA - dateB;
  return direction === 'asc' ? comparison : -comparison;
};

export const sortByOwner = (
  a: ExplorerNode,
  b: ExplorerNode,
  direction: SortDirection,
): number => {
  if (direction === null) {
    return 0;
  }
  const ownerA = a?.extra?.owner || '';
  const ownerB = b?.extra?.owner || '';
  const comparison = ownerA.localeCompare(ownerB);
  return direction === 'asc' ? comparison : -comparison;
};

export const sortNodes = (
  nodes: ExplorerNode[],
  column: DetailViewSortColumn | null,
  direction: SortDirection,
): ExplorerNode[] => {
  if (!column || direction === null) {
    return nodes;
  }

  return [...nodes].sort((a, b) => {
    switch (column) {
      case EDetailViewCols.Name:
        return sortByFileName(a, b, direction);
      case EDetailViewCols.DatePrepared:
        return sortByDate(a, b, EDetailViewCols.DatePrepared, direction);
      case EDetailViewCols.LastImaged:
        return sortByDate(a, b, EDetailViewCols.LastImaged, direction);
      case EDetailViewCols.Owner:
        return sortByOwner(a, b, direction);
      case EDetailViewCols.DateDispensed:
        return sortByDate(a, b, EDetailViewCols.DateDispensed, direction);
      default:
        return 0;
    }
  });
};

export interface FilterMap {
  filterType: EDetailViewFilterTypes;
  column: string;
  value: any;
}

export interface DateRangeFilter {
  startDate?: string;
  endDate?: string;
}

const getNodeColumnValue = (node: ExplorerNode, column: string): string => {
  switch (column) {
    case EDetailViewCols.Name:
      return node?.name ?? '';
    case EDetailViewCols.Owner:
      return node?.extra?.owner ?? '';
    default:
      return node?.[column] ?? '';
  }
};

export const filterNodes = (
  nodes: ExplorerNode[],
  filters?: FilterMap[],
): ExplorerNode[] => {
  if (!filters || filters.length === 0) {
    return nodes;
  }
  let filteredData = [...nodes];
  filters.forEach((filter) => {
    if (filter.filterType === EDetailViewFilterTypes.Text) {
      filteredData = filteredData.filter((n) => {
        const val = getNodeColumnValue(n, filter.column);
        return (val as string)
          .toLowerCase()
          .includes((filter.value as string).toLowerCase());
      });
    } else if (filter.filterType === EDetailViewFilterTypes.DateRange) {
      filteredData = filterByDateRange(
        filteredData,
        (item: ExplorerNode) => getNodeColumnValue(item, filter.column),
        (filter.value as DateRangeFilter).startDate ?? null,
        (filter.value as DateRangeFilter).endDate ?? null,
      );
    }
  });

  return filteredData;
};

const parseDate = (value: string): Date => {
  // Handle YYYY-MM-DD vs DD-MM-YYYY
  const parts = value.split(/[\/\-]/).map(Number);
  if (parts[0] > 999) {
    const [year, month, day] = parts;
    return new Date(year, month - 1, day);
  }
  const [day, month, year] = parts;
  return new Date(year, month - 1, day);
};

const filterByDateRange = (
  data: any[],
  getDate: (item: any) => string,
  startDate: string | null,
  endDate: string | null,
): any[] => {
  if (!startDate || !endDate) {
    return data;
  }
  const start = parseDate(startDate);
  const end = parseDate(endDate);
  return data.filter((item) => {
    const rawDate = getDate(item);
    if (!rawDate) {
      return false;
    }
    const itemDate = parseDate(rawDate);
    return itemDate >= start && itemDate <= end;
  });
};

export const getViewTypeSvgIconName = (viewType: EViewType): RMSvgIconNames => {
  switch (viewType) {
    case EViewType.ListView:
      return 'ListView';
    case EViewType.ContentView:
      return 'ContentView';
    case EViewType.ThumbnailView:
      return 'ThumbnailView';
    case EViewType.DetailView:
      return 'DetailView';
  }
};
